import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import i18n from 'i18next';
import classnames from 'classnames';
import { useQueryClient } from '@tanstack/react-query';

import LockOutlined from '@mui/icons-material/LockOutlined';

import { Typo } from 'uikit/content';
import { pick, get } from 'lodash';

import { Form, ModificationTrackingForm } from 'core/forms';

import { addStylesToSchema, useGlobalQueries } from '@/exceed';

import convertNulls from '../helpers/convertNulls';
import getIsSocialAppEnabled from '../helpers/getIsSocialAppEnabled';
import getIsSocialFeatureEnabled from '../helpers/getIsSocialFeatureEnabled';

import useVideoVersion from '../hooks/useVideoVersion';

import getVideoAdminPropertiesGeneralSchema from '../schemas/getVideoAdminPropertiesGeneralSchema';
import getVideoAdminPropertiesDisplaySchema from '../schemas/getVideoAdminPropertiesDisplaySchema';
import getVideoAdminPropertiesVideoSchema from '../schemas/getVideoAdminPropertiesVideoSchema';
import getVideoAdminPropertiesAdditionalDetailsSchema from '../schemas/getVideoAdminPropertiesAdditionalDetailsSchema';
import getVideoAdminPropertiesAssociationsSchema from '../schemas/getVideoAdminPropertiesAssociationsSchema';
import getVideoAdminPropertiesCatalogPropertiesSchema from '../schemas/getVideoAdminPropertiesCatalogPropertiesSchema';
import getVideoAdminPropertiesSocialSchema from '../schemas/getVideoAdminPropertiesSocialSchema';
import getVideoAdminPropertiesCustomFieldsSchema from '../schemas/getVideoAdminPropertiesCustomFieldsSchema';
import getVideoAdminPropertiesSeoPropertiesSchema from '../schemas/getVideoAdminPropertiesSeoPropertiesSchema';
import getVideoAdminPropertiesTaggingSchema from '../schemas/getVideoAdminPropertiesTaggingSchema';
import getVideoAdminPropertiesAdvancedPropertiesSchema from '../schemas/getVideoAdminPropertiesAdvancedPropertiesSchema';
import getVideoAdminPropertiesPublicPropertiesSchema from '../schemas/getVideoAdminPropertiesPublicPropertiesSchema';
import getVideoAdminPropertiesEnrollmentSchema from '../schemas/getVideoAdminPropertiesEnrollmentSchema';

import styles from '../styles/videosAdminPropertiesGeneral.module.scss';

const PROPERTY_SCHEMA_MAPPING = {
  general: getVideoAdminPropertiesGeneralSchema,
  video: ({ videoProperties, videoPropertiesKey }) =>
    getVideoAdminPropertiesVideoSchema({
      label: i18n.t('choose-video'),
      help: i18n.t('choose-video-help'),
      videoProperties,
      videoPropertiesKey,
    }),
  video_trailer: ({ videoProperties, videoPropertiesKey }) =>
    getVideoAdminPropertiesVideoSchema({
      label: i18n.t('video-trailer'),
      help: i18n.t('video-trailer-help'),
      videoProperties,
      videoPropertiesKey,
    }),
  display: getVideoAdminPropertiesDisplaySchema,
  additional_details: getVideoAdminPropertiesAdditionalDetailsSchema,
  associations: getVideoAdminPropertiesAssociationsSchema,
  social: getVideoAdminPropertiesSocialSchema,
  custom_fields: getVideoAdminPropertiesCustomFieldsSchema,
  catalog: getVideoAdminPropertiesCatalogPropertiesSchema,
  public_properties: getVideoAdminPropertiesPublicPropertiesSchema,
  tagging: getVideoAdminPropertiesTaggingSchema,
  seo_properties: getVideoAdminPropertiesSeoPropertiesSchema,
  advanced_properties: getVideoAdminPropertiesAdvancedPropertiesSchema,
  enrollment: getVideoAdminPropertiesEnrollmentSchema,
};

const NON_TRACKED_FIELDS = ['enrollment_url', 'enrollment_url_buttons', 'letter_triggers'];

const VideosAdminPropertiesGeneralForm = ({
  section,
  videoProperties,
  videoPropertiesKey,
  isChildLocale,
  isDisabled,
  onCancel,
  onSubmit,
  onUpdate,
}) => {
  const { adminSettings } = useGlobalQueries();
  const { id, type } = useParams();
  const handleSubmit = (args) => onSubmit({ ...args, section });
  const queryClient = useQueryClient();
  const { videoVersionKey } = useVideoVersion({ isEnabled: false });

  const originalSchema = PROPERTY_SCHEMA_MAPPING[section]({
    adminSettings,
    id,
    isChildLocale,
    isInLiveMode: type === 'live',
    onSubmit: handleSubmit,
    queryClient,
    videoProperties,
    videoPropertiesKey,
    videoVersionKey,
  });

  const model = get(videoProperties.data, section, {});

  const title = i18n.t(`${section}-settings`);
  const isSocialSection = section === 'social';
  const isUnpurchasedSocialSection = isSocialSection && !getIsSocialAppEnabled({ adminSettings });

  const isDisabledSocialSection =
    isSocialSection && !getIsSocialFeatureEnabled({ adminSettings, feature: 'Community' });

  const schemas = useMemo(() => {
    const groups = [];
    let trackedGroup = [];
    let nonTrackedGroup = [];

    const addGroupToSchemas = () => {
      if (trackedGroup.length) {
        groups.push({ isTracked: true, schema: Object.fromEntries(trackedGroup) });
        trackedGroup = [];
      }
      if (nonTrackedGroup.length) {
        groups.push({ isTracked: false, schema: Object.fromEntries(nonTrackedGroup) });
        nonTrackedGroup = [];
      }
    };

    Object.entries(originalSchema).forEach((field) => {
      if (NON_TRACKED_FIELDS.includes(field[0])) {
        if (trackedGroup.length) addGroupToSchemas();
        nonTrackedGroup.push(field);
        return;
      }
      if (nonTrackedGroup.length) addGroupToSchemas();
      trackedGroup.push(field);
    });

    addGroupToSchemas();

    return groups;
  }, [originalSchema]);

  if (!schemas.length) return null;

  return (
    <section>
      <Typo
        element="h2"
        variant="h5"
        modifier="semi"
        className={classnames(styles.title, {
          [styles.isDisabledSocialTitle]: isUnpurchasedSocialSection,
        })}
      >
        {title}
        {isUnpurchasedSocialSection && (
          <LockOutlined className={styles.icon} sx={{ height: 20, width: 20 }} />
        )}
      </Typo>
      {schemas.map(({ isTracked, schema }, index) => {
        const key = `${section}-${index}`;

        if (!isTracked) {
          return (
            <div className={styles.section} key={key}>
              <Form isDisabled={isDisabled} model={model} schema={schema} />
            </div>
          );
        }

        return (
          <ModificationTrackingForm
            className={classnames(styles.section, styles.isTracked, {
              [styles.isDisabledSocialSection]:
                isUnpurchasedSocialSection || isDisabledSocialSection,
            })}
            key={key}
            model={convertNulls({ data: pick(model, Object.keys(schema)), schema })}
            modifiedClassName={styles.isModified}
            schema={addStylesToSchema(schema, styles)}
            onCancelClick={(args) => onCancel({ ...args, schema })}
            onSaveClick={handleSubmit}
            onFormUpdate={onUpdate}
            isDisabled={isDisabled}
          />
        );
      })}
    </section>
  );
};

VideosAdminPropertiesGeneralForm.propTypes = {};

export default React.memo(VideosAdminPropertiesGeneralForm);
